<script lang="ts">
    import { getI18n } from '~/stores/i18n';
    import { type WorkDetailHeader } from '~/jet/models';

    // Components
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import ClickAction from '~/components/jet/action/ClickAction.svelte';
    import CloudButtons from '~/components/jet/buttons/CloudButtons.svelte';
    import ShareIcon from '~assets/icons/share.svg';

    // Shared
    import Truncate from '@amp/web-app-components/src/components/Truncate/Truncate.svelte';

    // Utils
    import { shareLinkOrCopy } from '~/utils/share';

    export let header: WorkDetailHeader;

    let titleHeight: number;

    const i18n = getI18n();
    const MAX_PARAGRAPH_LINES = 3;
    const TWO_LINE_TITLE_LARGE = 64;
    const TWO_LINE_TITLE_SMALL = 52;

    $: composerAction = header.composerAction;
    $: title = header.title;
    $: subtitle = header.subtitle;
    $: tertiaryTitle = header.composerName;
    $: modal = header.editorialNotes;
    $: modalTitle = modal?.title;
    $: modalText = modal?.standard;

    $: updateTitleSize =
        titleHeight > TWO_LINE_TITLE_LARGE ||
        titleHeight === TWO_LINE_TITLE_SMALL;
</script>

<div class="work-header-container">
    <div class="work-header__composer" data-testid="work-header-composer">
        <ClickAction action={composerAction}>
            {tertiaryTitle}
        </ClickAction>
    </div>

    <h1
        class="work-header__title"
        class:work-header__title--fluid-size={updateTitleSize}
        data-testid="work-header-title"
        bind:clientHeight={titleHeight}
    >
        {title}
    </h1>

    {#if subtitle}
        <div class="work-header__subtitle" data-testid="work-header-subtitle">
            {subtitle}
        </div>
    {/if}

    {#if modal}
        <div class="work-header__description">
            <Truncate
                text={modalText}
                lines={MAX_PARAGRAPH_LINES}
                title={modalTitle}
                modalType="contentModal"
                translateFn={$i18n.t}
            />
        </div>
    {/if}

    <div class="work-header__secondary-actions">
        <CloudButtons id={header?.id} type={header?.type} />
        {#if header.contextMenuAction}
            <ProductContextMenu contextMenuAction={header?.contextMenuAction} />
        {:else if header.shareButton}
            <span class="work-header__share-button">
                <button
                    data-testid="share-button"
                    aria-label={header.shareButton.title}
                    class="share-button"
                    on:click={() =>
                        shareLinkOrCopy({
                            url: header.shareButton?.action?.url,
                        })}
                >
                    <ShareIcon />
                </button>
            </span>
        {/if}
    </div>
</div>

<style lang="scss">
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .work-header-container {
        padding-top: 20px;
        color: var(--systemPrimary);
        margin-inline-start: var(--bodyGutter);
        margin-inline-end: var(--bodyGutter);
        padding-bottom: 24px;
        border-bottom: var(--keyline-border-style);
        display: grid;
        grid-template-areas:
            '. actions'
            'composer    composer'
            'title       title'
            'subtitle    subtitle'
            'description description';

        // Collapse empty subtitle/description rows when not present
        grid-auto-rows: min-content;

        @media (--small) {
            padding-top: 40px;
            grid-template-areas:
                'composer    composer'
                'title       title'
                'subtitle    actions'
                'description actions';
            column-gap: 10px;
        }
    }

    .work-header__composer {
        grid-area: composer;
        font: var(--title-3-emphasized);

        @include line-clamp(3);

        :global(.click-action:hover) {
            color: var(--keyColor);
        }
    }

    .work-header__title {
        grid-area: title;
        font: var(--large-title-emphasized-serif);
        margin-top: 8px;

        @include line-clamp(9);
    }

    .work-header__title--fluid-size {
        font: var(--title-1-emphasized-serif);
    }

    .work-header__subtitle {
        grid-area: subtitle;
        font: var(--body);
        color: var(--systemSecondary);
        margin-top: 6px;
    }

    .work-header__description {
        grid-area: description;
        margin-top: 14px;
        max-width: 570px;
    }

    .work-header__secondary-actions {
        --contextMenuButtonSize: #{$action-button-size};
        height: 30px;
        grid-area: actions;
        display: flex;
        justify-content: end;
        gap: 10px;
        align-items: flex-start;

        :global(.more-button) {
            --contextMenuCircleFillOverride: var(--systemQuinary);
            --contextMenuEllipsisFillOverride: var(--keyColor);
            --contextMenuCircleHoverFill: var(--systemQuinary);
            --contextMenuEllipsisHoverFill: var(--keyColor);
        }

        @media (--small) {
            height: auto;
            align-items: end;
        }
    }

    .work-header__share-button {
        --shareButtonBackground: var(--systemQuinary);
        --shareIconColor: var(--keyColor);
        width: $action-button-size;
        height: $action-button-size;
        border-radius: 50%;
    }

    .share-button {
        background: var(--shareButtonBackground);
        border-radius: inherit;
        width: 100%;
        height: 100%;
        color: var(--shareIconColor);

        :global(svg) {
            width: 13px;
            transform: translateY(-1px);
        }
    }
</style>
